import { getStoredFilters, HeaderOptions, updateStoredFilters } from '../common/reports-constants';
import Header from '../../../components/common/global-top-bar';
import SwitchBar from '../../../components/switch-bar';
import { useState } from 'react';
import { useCallOutcomesReportDownload, useCallSummaryReportDownload } from '../../../hooks/useCallReporting';
import { DebounceInput } from 'react-debounce-input';
import { CallsSummary } from './summary';
import { CallReportFilters } from './common/call-report-constants';
import { CallsDetails } from './details';
import { useAllMinimalClients } from '../../../hooks/useClients';
enum CallReportType {
  CallsSummary = 'Calls Summary',
  CallDetails = 'Calls Details',
}

const CallReporting: React.FC = () => {
  const today = new Date();
  const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

  const [filters, setFilters] = useState<CallReportFilters>(() => {
    const defaultFilters: CallReportFilters = {
      startDate: firstDayOfCurrentMonth.toISOString().split('T')[0],
      endDate: firstDayOfNextMonth.toISOString().split('T')[0],
      listenerRoleId: undefined,
      clientId: undefined,
    };
    return getStoredFilters(defaultFilters);
  });
  const [reportType, setReportType] = useState<CallReportType>(CallReportType.CallsSummary);

  return (
    <div>
      <Header heading={'Call Reporting'} />
      <SwitchBar heading={HeaderOptions} position={0} />
      <Filters filters={filters} setFilters={setFilters} reportType={reportType} setReportType={setReportType} />

      <div>
        <div className="max-window-height-connections overflow-y-auto px-7 pt-7 font-['Montserrat']">
          {reportType === CallReportType.CallsSummary && <CallsSummary filters={filters} />}
          {reportType === CallReportType.CallDetails && <CallsDetails filters={filters} />}
        </div>
      </div>
    </div>
  );
};

interface FilterProps {
  filters: CallReportFilters;
  setFilters: (filters: CallReportFilters) => void;
  reportType: CallReportType;
  setReportType: (reportType: CallReportType) => void;
}

const Filters: React.FC<FilterProps> = ({ filters, setFilters, reportType, setReportType }) => {
  const { data: clients } = useAllMinimalClients();
  const { isFetching: isFetchingOutcomes, refetch: refetchOutcomes } = useCallOutcomesReportDownload(filters, false);
  const { isFetching: isFetchingSummary, refetch: refetchSummary } = useCallSummaryReportDownload(filters, false);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
    updateStoredFilters(name, value);
  };

  const handleInputChange = (filter: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilters({
      ...filters,
      [filter]: value && value.length ? parseInt(event.target.value) : undefined,
    });
  };

  const handleDownload = () => {
    if (filters.startDate !== '' && filters.endDate !== '') {
      const fetchData = async () => {
        (await reportType) === CallReportType.CallDetails ? refetchOutcomes() : refetchSummary();
      };
      fetchData();
    }
  };

  return (
    <div className="gray-border-line bg-gray-background-light flex h-10 w-full items-center space-x-2 border-b px-4">
      <div className="gray-background-dark flex items-center py-4">
        {(Object.keys(CallReportType) as Array<keyof typeof CallReportType>).map((item) => (
          <button
            key={item}
            className={
              'whitespace-nowrap px-2 py-1 text-sm' +
              (CallReportType[item] === reportType
                ? ' bg-blue-primary text-white'
                : ' bg-gray-background-dark text-gray-dark')
            }
            onClick={() => {
              setReportType(CallReportType[item]);
            }}
          >
            {CallReportType[item]}
          </button>
        ))}
      </div>
      <div className="flex items-center space-x-2">
        <label className="whitespace-nowrap text-sm">Start Date:</label>
        <input
          type="date"
          name="startDate"
          value={filters.startDate}
          onChange={handleDateChange}
          className="w-24 rounded border p-0.5 text-xs focus:ring-transparent"
        />
        <label className="whitespace-nowrap text-sm">End Date:</label>
        <input
          type="date"
          name="endDate"
          value={filters.endDate}
          onChange={handleDateChange}
          className="w-24 rounded border p-0.5 text-xs focus:ring-transparent"
        />
      </div>
      <div className="flex items-center space-x-1">
        <label className="flex items-center whitespace-nowrap text-sm">
          Group:
          <select
            className="ml-1 h-6 w-60 rounded px-1 py-0.5 text-sm focus:ring-transparent"
            onChange={(e) => {
              const value = e.target.value;
              setFilters({
                ...filters,
                clientId: value ? parseInt(value) : undefined,
              });
            }}
          >
            <option value="">All</option>
            {clients?.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center whitespace-nowrap text-sm">
          Listener ID:
          <DebounceInput
            className="ml-1 h-6 w-20 rounded p-0.5 text-center text-sm focus:ring-transparent"
            debounceTimeout={300}
            onChange={(e) => handleInputChange('listenerRoleId', e)}
            value={filters.listenerRoleId || ''}
          />
        </label>
      </div>
      <div>
        <button
          className="bg-blue-primary rounded-full px-4 py-1 text-sm text-white"
          onClick={handleDownload}
          disabled={isFetchingOutcomes || isFetchingSummary}
        >
          Download
        </button>
      </div>
    </div>
  );
};

export default CallReporting;
